import React, { useEffect, useRef, useState } from 'react';
import queryString from 'query-string';
import './assets/css/App.css';
import BingoCard from './BingoCard';
import VerifiedTileNotifications from './components/notifications/VerifiedTileNotifications';
import WhenPlane from './components/whenplane/WhenPlane';
import useLiveShow from './components/live/liveShowStore';
import { registerBaseTheme, applyAfterDark } from './afterDarkTheme';

function App() {
  // The look actually on screen. Usually the ?theme= the visitor asked for, but
  // when the show calls After Dark the dark stylesheet takes over on top of it,
  // and this flips at the exact instant the sheets swap behind the fade so the
  // logo changes with everything else rather than a beat early.
  const [look, setLook] = useState('default');

  const live = useLiveShow();
  const baseTheme = useRef('default');
  const pinned = useRef(false);
  const settled = useRef(false);
  // Nothing about the lights may happen before the visitor's own stylesheet is
  // in the document: which sheet wins is decided by which one came last (see
  // src/afterDarkTheme.js). Working out whose theme it is can now take a round
  // trip, so the After Dark effect below waits on this rather than racing it.
  const [themeReady, setThemeReady] = useState(false);

  // WHICH LOOK WINS, in this order:
  //   1. ?theme= in the address -- an explicit request for this one page, so
  //      shared links and a quick look at a theme keep working as they always
  //      have, signed in or not;
  //   2. the signed-in player's saved choice (GET /api/account/me), which is
  //      the whole point: a look you unlocked should just be there;
  //   3. the everyday card.
  // A live After Dark then goes over the top of whichever of the three it was,
  // and hands it straight back when the call ends -- see the effect below.
  useEffect(() => {
    const params = queryString.parse(window.location.search);
    const asked = params.theme ? String(params.theme) : null;

    // Whatever we settle on, we settle on ONCE: a slow answer that arrives
    // after the fallback has already been applied must not restyle the page
    // under somebody who is by then reading it.
    let done = false;
    const start = (chosen) => {
      if (done) return;
      done = true;
      const initialTheme = chosen || 'default';
      setLook(initialTheme);
      baseTheme.current = initialTheme;
      // Somebody on After Dark by their own choice -- typed into the address or
      // saved to their account -- has already chosen this look. The automatic
      // layer stays out of their way, and must never "undo" a theme they picked
      // themselves when the panel ends the call.
      pinned.current = initialTheme === 'afterdark';

      // The theme-specific stylesheet, loaded exactly as it always was -- but
      // through registerBaseTheme, which remembers what got injected so After
      // Dark can switch it off and back on again later.
      registerBaseTheme(() => {
        switch (initialTheme) {
          case 'floatplane':
            require('./assets/css/BingoCard-FloatPlane.css');
            break;
          case 'ltx23':
            require('./assets/css/BingoCard-LTX23.css');
            break;
          case 'christmas23':
            require('./assets/css/BingoCard-Christmas23.css');
            break;
          case 'bread':
            require('./assets/css/BingoCard-BREAD.css');
            break;
          case 'winter':
            require('./assets/css/BingoCard-WINTER23.css');
            break;
          case 'winterdark':
            require('./assets/css/BingoCard-WINTER23DARK.css');
            break;
          case 'afterdark':
            require('./assets/css/BingoCard-AFTERDARK.css');
            break;
          case 'bringus':
            require('./assets/css/BingoCard-BRINGUS-DEMO.css');
            break;
          default:
            require('./assets/css/BingoCard.css');
        }
      });
      setThemeReady(true);
    };

    // An address-bar theme needs no round trip and gets none.
    if (asked) { start(asked); return undefined; }

    // Otherwise ask who this is. Same origin, no account needed to ask, and
    // every way it can go wrong -- signed out, service down, nonsense back,
    // nothing back at all -- lands on the everyday card, which is exactly what
    // an anonymous visitor got before any of this existed.
    let dropped = false;
    const watchdog = setTimeout(() => start(null), 2500);
    fetch('/api/account/me', { credentials: 'same-origin', cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => { if (!dropped) start(data && data.player ? data.player.site_theme : null); })
      .catch(() => { if (!dropped) start(null); });

    return () => { dropped = true; clearTimeout(watchdog); };
  }, []);

  // After Dark, applied to everybody at once and to anybody who arrives after
  // the call. `live.afterDark` is settled on mount from the socket AND over
  // plain HTTP (see components/live/liveShowStore.js), so this is right for a
  // late joiner and for a signed-out visitor, neither of whom is waiting on an
  // event that already happened.
  const afterDarkOn = !!(live.afterDark && live.afterDark.on);

  useEffect(() => {
    // The base sheet has to be in the document before this one lands on top.
    if (!themeReady) return;
    if (pinned.current) return;
    // The first answer about tonight lands instantly: opening the page during
    // After Dark should ARRIVE dark, not perform the change at somebody who was
    // not there for it. Every change after that is worth the fade.
    const animate = settled.current;
    applyAfterDark(afterDarkOn, {
      animate,
      onSwap: () => setLook(afterDarkOn ? 'afterdark' : baseTheme.current),
    });
    if (live.ready) settled.current = true;
  }, [afterDarkOn, live.ready, themeReady]);

  const backgroundStyle = {
    backgroundImage: `linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 100%), url(https://cdn.wanshow.bingo/resources/vendor/img/wan-stream-thumbnail.jpeg)`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    transition: 'background-image 1s ease-in-out',
    zIndex: -3
  };

  return (
    <div className="App" style={backgroundStyle}> {/* Apply backgroundStyle here */}
      <VerifiedTileNotifications />
      <BingoCard theme={look} /> {/* the look currently on screen, not just the URL's */}
    </div>
  );
}

export default App;
